/**
 * AIImageGenerationModal Component
 *
 * Modal for generating AI images with prompt customization,
 * color palette selection, and aspect ratio configuration.
 * Uses the agent queue system with image_generator agent.
 *
 * States:
 * - CONFIGURE: Form to set up generation parameters
 * - GENERATING: Loading state with progress tracking
 * - SELECT: Grid of variations for selection
 */

import { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, RefreshCw, Pencil, Check, Palette, ImageIcon, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { ImageVariationsGrid, GeneratedImage } from './ImageVariationsGrid';
import { THEME_PALETTES, ThemePalette } from './ThemePaletteSelector';
import {
  executeAndSubscribe,
  cancelAgentJob,
  AgentJobProgress,
} from '../../services/agentJobService';
import api from '../../services/api';
import { logger } from '../../utils/logger';
import { useUserStore } from '../../stores/userStore';

type ModalState = 'CONFIGURE' | 'GENERATING' | 'SELECT';
type AspectRatio = '1:1' | '16:9' | '4:3';
type VariationCount = 2 | 3 | 4;

interface AIImageGenerationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPrompt?: string;
  onImageApproved: (imageUrl: string) => void;
  /** Optional conversationId - if not provided, a temporary conversation will be created */
  conversationId?: string;
  /** Mode: 'slide' allows text (for reference images), 'default' enforces no text */
  mode?: 'default' | 'slide';
}

const ASPECT_RATIO_OPTIONS: { value: AspectRatio; label: string; icon: string }[] = [
  { value: '16:9', label: '16:9', icon: '▬' },
  { value: '1:1', label: '1:1', icon: '■' },
  { value: '4:3', label: '4:3', icon: '▭' },
];

const VARIATION_OPTIONS: VariationCount[] = [2, 3, 4];

export function AIImageGenerationModal({
  open,
  onOpenChange,
  initialPrompt = '',
  onImageApproved,
  conversationId: propConversationId,
  mode = 'slide',
}: AIImageGenerationModalProps) {
  // Get user context for conversation creation
  const { activeOrganization, activeTeam } = useUserStore();

  // State
  const [modalState, setModalState] = useState<ModalState>('CONFIGURE');
  const [prompt, setPrompt] = useState(initialPrompt);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
  const [variationCount, setVariationCount] = useState<VariationCount>(4);
  const [selectedPalette, setSelectedPalette] = useState<ThemePalette | null>(null);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Agent job state
  const [progress, setProgress] = useState<AgentJobProgress | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [tempConversationId, setTempConversationId] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount or modal close
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt);
      setModalState('CONFIGURE');
      setGeneratedImages([]);
      setSelectedImageIndex(null);
      setError(null);
      setProgress(null);
      setCurrentJobId(null);
    } else {
      // Cleanup when modal closes
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      // Delete temp conversation when modal closes (fire and forget)
      if (tempConversationId) {
        deleteTempConversation();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrompt]);

  // Create a temporary conversation for image generation
  const createTempConversation = async (): Promise<string> => {
    // Check if we already have a temp conversation
    if (tempConversationId) {
      return tempConversationId;
    }

    // Ensure we have org/team context
    if (!activeOrganization || !activeTeam) {
      throw new Error('No active organization or team. Please log in again.');
    }

    logger.info('[AIImageGenModal] Creating temporary conversation for image generation');

    const response = await api.post('/conversations', {
      title: 'Image Generation (Temporary)',
      collectionType: 'SLIDE',
      organizationId: activeOrganization.id,
      teamId: activeTeam.id,
      metadata: { isTemporary: true, purpose: 'image_generation' },
    });

    const newConvId = response.data.conversation.id;
    setTempConversationId(newConvId);
    logger.info(`[AIImageGenModal] Created temp conversation: ${newConvId}`);
    return newConvId;
  };

  // Delete temporary conversation (fire and forget)
  const deleteTempConversation = async () => {
    if (!tempConversationId) return;

    const convIdToDelete = tempConversationId;
    setTempConversationId(null); // Clear immediately to prevent duplicate deletes

    try {
      logger.info(`[AIImageGenModal] Deleting temp conversation: ${convIdToDelete}`);
      await api.delete(`/conversations/${convIdToDelete}`);
      logger.info(`[AIImageGenModal] Temp conversation deleted: ${convIdToDelete}`);
    } catch (err) {
      // Log but don't fail - temp conversations can be cleaned up later
      logger.warn(`[AIImageGenModal] Failed to delete temp conversation:`, err);
    }
  };

  // Handle generation using agent queue
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setError(null);
    setProgress(null);
    setModalState('GENERATING');

    try {
      // Get or create conversation
      const conversationId = propConversationId || await createTempConversation();

      logger.info(`[AIImageGenModal] Starting image generation with agent queue`, {
        conversationId,
        mode,
        aspectRatio,
        numberOfImages: variationCount,
      });

      // Prepare inputs for the image_generator agent
      const agentInputs: Record<string, any> = {
        prompt: prompt.trim(),
        aspectRatio,
        numberOfImages: variationCount,
        mode,
        imageModel: 'gemini', // Use Gemini native for slide mode
      };

      // Add palette if selected (as JSON string)
      if (selectedPalette) {
        agentInputs.palette = JSON.stringify(selectedPalette.colors);
      }

      // Execute agent and subscribe to updates
      const { jobId, unsubscribe } = await executeAndSubscribe(
        {
          conversationId,
          agentName: 'image_generator',
          inputs: agentInputs,
        },
        {
          onProgress: (prog) => {
            setProgress(prog);
            logger.debug(`[AIImageGenModal] Progress: ${prog.percentage}% - ${prog.currentOperation}`);
          },
          onComplete: (result) => {
            logger.info(`[AIImageGenModal] Generation complete - raw result:`, JSON.stringify(result, null, 2));
            logger.info(`[AIImageGenModal] Result keys:`, Object.keys(result));

            // Extract image URLs from result
            // The agent returns { result: "{...stringified JSON...}", terminate_reason: "GOAL" }
            // We need to parse the nested result string
            let imageUrls: string[] = [];
            try {
              if (result.result && typeof result.result === 'string') {
                logger.info(`[AIImageGenModal] Parsing nested result string`);
                const parsedResult = JSON.parse(result.result);
                logger.info(`[AIImageGenModal] Parsed result keys:`, Object.keys(parsedResult));
                imageUrls = parsedResult.imageUrls || parsedResult.image_urls || [];
                logger.info(`[AIImageGenModal] Found ${imageUrls.length} images in parsed result`);
              } else if (result.imageUrls) {
                logger.info(`[AIImageGenModal] Using result.imageUrls directly`);
                imageUrls = result.imageUrls;
              } else if (result.image_urls) {
                logger.info(`[AIImageGenModal] Using result.image_urls directly`);
                imageUrls = result.image_urls;
              } else {
                logger.warn(`[AIImageGenModal] No imageUrls field found in result`);
              }
            } catch (parseError) {
              logger.error(`[AIImageGenModal] Error parsing result:`, parseError);
              logger.error(`[AIImageGenModal] result.result value:`, result.result);
            }

            if (imageUrls.length === 0) {
              setError('No images were generated');
              setModalState('CONFIGURE');
              return;
            }

            // Convert to GeneratedImage format
            const images: GeneratedImage[] = imageUrls.map((url: string) => ({
              url,
              width: aspectRatio === '16:9' ? 1920 : aspectRatio === '1:1' ? 1024 : 1440,
              height: aspectRatio === '16:9' ? 1080 : aspectRatio === '1:1' ? 1024 : 1080,
            }));

            setGeneratedImages(images);
            setSelectedImageIndex(0);
            setModalState('SELECT');
          },
          onError: (errorMsg) => {
            logger.error(`[AIImageGenModal] Generation failed:`, errorMsg);
            setError(errorMsg);
            setModalState('CONFIGURE');
          },
        }
      );

      setCurrentJobId(jobId);
      unsubscribeRef.current = unsubscribe;
    } catch (err) {
      logger.error(`[AIImageGenModal] Error starting generation:`, err);
      setError(err instanceof Error ? err.message : 'Failed to start image generation');
      setModalState('CONFIGURE');
    }
  };

  // Handle cancel during generation
  const handleCancel = async () => {
    if (currentJobId) {
      try {
        await cancelAgentJob(currentJobId);
        logger.info(`[AIImageGenModal] Cancelled job: ${currentJobId}`);
      } catch (err) {
        logger.warn(`[AIImageGenModal] Error cancelling job:`, err);
      }
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    setCurrentJobId(null);
    setProgress(null);
    setModalState('CONFIGURE');
  };

  // Handle approval
  const handleApprove = () => {
    if (selectedImageIndex === null || !generatedImages[selectedImageIndex]) {
      return;
    }

    onImageApproved(generatedImages[selectedImageIndex].url);
    onOpenChange(false);
  };

  // Handle regenerate
  const handleRegenerate = () => {
    setModalState('CONFIGURE');
    setGeneratedImages([]);
    setSelectedImageIndex(null);
  };

  // Render palette selector
  const renderPaletteSelector = () => (
    <div className="space-y-2">
      <label className="text-sm font-medium flex items-center gap-2">
        <Palette className="h-4 w-4" />
        Color Palette (Optional)
      </label>
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border">
        {/* No palette option */}
        <button
          type="button"
          onClick={() => setSelectedPalette(null)}
          className={cn(
            'flex-shrink-0 p-2 rounded-lg border transition-all',
            'hover:border-primary/50',
            !selectedPalette
              ? 'border-primary bg-primary/10'
              : 'border-border/50'
          )}
        >
          <div className="w-16 h-8 flex items-center justify-center text-xs text-muted-foreground">
            Auto
          </div>
        </button>

        {THEME_PALETTES.map((palette) => (
          <button
            key={palette.id}
            type="button"
            onClick={() => setSelectedPalette(palette)}
            className={cn(
              'flex-shrink-0 p-2 rounded-lg border transition-all',
              'hover:border-primary/50',
              selectedPalette?.id === palette.id
                ? 'border-primary bg-primary/10'
                : 'border-border/50'
            )}
          >
            <div className="flex gap-1 mb-1">
              {Object.values(palette.colors).slice(0, 4).map((color, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <p className="text-[10px] text-center truncate w-16">
              {palette.name}
            </p>
          </button>
        ))}
      </div>
    </div>
  );

  // Render configure state
  const renderConfigure = () => (
    <div className="space-y-4">
      {/* Prompt */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Describe your image</label>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A modern, minimalist slide background with abstract geometric shapes..."
          className="min-h-[100px] resize-none"
          maxLength={2000}
        />
        <p className="text-xs text-muted-foreground text-right">
          {prompt.length}/2000
        </p>
      </div>

      {/* Aspect Ratio */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          Aspect Ratio
        </label>
        <div className="flex gap-2">
          {ASPECT_RATIO_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setAspectRatio(option.value)}
              className={cn(
                'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all',
                'hover:border-primary/50',
                aspectRatio === option.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border/50'
              )}
            >
              <span className="mr-1">{option.icon}</span> {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Variations */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Number of Variations</label>
        <div className="flex gap-2">
          {VARIATION_OPTIONS.map((count) => (
            <button
              key={count}
              type="button"
              onClick={() => setVariationCount(count)}
              className={cn(
                'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all',
                'hover:border-primary/50',
                variationCount === count
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border/50'
              )}
            >
              {count}
            </button>
          ))}
        </div>
      </div>

      {/* Palette Selector */}
      {renderPaletteSelector()}

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}
    </div>
  );

  // Render generating state with progress
  const renderGenerating = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-4">
      <div className="relative">
        <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
        <div className="relative w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      </div>
      <div className="text-center space-y-2">
        <p className="font-medium">Generating your images...</p>
        {progress ? (
          <>
            <div className="w-64 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300 bg-primary"
                style={{ width: `${progress.percentage || 0}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {progress.currentOperation || 'Processing...'}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            This may take up to 60 seconds
          </p>
        )}
      </div>
    </div>
  );

  // Render select state
  const renderSelect = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select an image to use as your reference for slide creation.
      </p>

      <ImageVariationsGrid
        images={generatedImages}
        selectedIndex={selectedImageIndex}
        onSelect={setSelectedImageIndex}
        aspectRatio={aspectRatio}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {modalState === 'SELECT' ? 'Choose Your Image' : 'Generate AI Image'}
          </DialogTitle>
          <DialogDescription>
            {modalState === 'CONFIGURE' &&
              'Describe the image you want to generate. The AI will create variations for you to choose from.'}
            {modalState === 'GENERATING' &&
              'Please wait while we generate your images...'}
            {modalState === 'SELECT' &&
              'Click on an image to select it, then approve to use it for your slide.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 overflow-y-auto flex-1 min-h-0">
          {modalState === 'CONFIGURE' && renderConfigure()}
          {modalState === 'GENERATING' && renderGenerating()}
          {modalState === 'SELECT' && renderSelect()}
        </div>

        <DialogFooter className="flex-shrink-0">
          {modalState === 'CONFIGURE' && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                className="bg-primary hover:bg-primary/90"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Images
              </Button>
            </>
          )}

          {modalState === 'GENERATING' && (
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}

          {modalState === 'SELECT' && (
            <>
              <Button
                variant="outline"
                onClick={handleRegenerate}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate
              </Button>
              <Button
                variant="outline"
                onClick={() => setModalState('CONFIGURE')}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit Prompt
              </Button>
              <Button
                onClick={handleApprove}
                disabled={selectedImageIndex === null}
                className="bg-primary hover:bg-primary/90"
              >
                <Check className="h-4 w-4 mr-2" />
                Use This Image
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AIImageGenerationModal;
